<template>
  <Section
    :id="id"
    :header="t('common.relatedParam', { param: t('glCodes.glCodes', 2) })"
    :loading="loading || isAnySaving"
    class="related-gl-code-section"
    data-class="flex flex-gap-1 flex-column"
  >
    <RelatedGLCodesForOrder
      v-for="(order, index) in orders"
      :key="order.id"
      :model-value="order"
      :loading="loading"
      :saving="isSaving[index]"
      :editable="editable"
      @update:model-value="onUpdate(index, $event)"
    />
  </Section>
</template>

<script lang="ts" setup>
import axios from 'axios';
import { computed, PropType, provide, Ref, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';

import api from '@/api';
import { Order } from '@/model';

import RelatedGLCodesForOrder from '@/components/glCode/RelatedGLCodesForOrder.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  orders: {
    type: Array as PropType<Order[]>,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: () => false,
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
});
const { id, orders, loading, editable } = toRefs(props);

const { t } = useI18n();
const toast = useToast();
// This prevents validation in any child component being collected by
// `ShipmentDetail` since the orders are saved independently from the shipment.
provide('scope', id.value);

const isSaving: Ref<boolean[]> = ref([]);

const isAnySaving = computed(() => {
  return isSaving.value.some((saving) => saving);
});

const onUpdate = async (index: number, newOrder: Order) => {
  try {
    isSaving.value[index] = true;
    const updatedOrder = await api.order.update(newOrder);
    orders.value.splice(index, 1, updatedOrder);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error saving related gl codes', e);
    toast.addError({
      detail: t('errors.savingParam', {
        param: t('common.relatedParam', {
          param: t('glCodes.glCodes', 2),
        }),
      }),
      error: e,
    });
  } finally {
    isSaving.value[index] = false;
  }
};
</script>
